/**
 * IntakeFormResponsePanel — viewer read-only de la `form_response` d'origine
 * sur la fiche `/intake-events/:id` (F2-Forms-1e-A).
 *
 * Contexte : une saisie directe conseiller (1d) crée un `intake_event`
 * `source_kind='human_ui'` avec `payload.form_response_id`. Sans ce panneau, les
 * réponses structurées sont invisibles et le courtier doit tout retaper à la
 * matérialisation. Ici on les rend en lecture seule, contre la **version exacte
 * immuable** liée (jamais « latest »).
 *
 * Web-only : réutilise `GET /v1/form-responses/:id` (tenant-scopé, client-safe)
 * via `useFormResponse` — aucune nouvelle API/DB. Aucun prefill/materialize/role
 * (→ 1e-B). Aucun `console.log` de la réponse.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { FormRenderer, type RenderSchema } from "../../../components/forms/FormRenderer";
import { normalizeLang } from "../../../lib/apiErrorMessage";
import { useFileDownloadUrl } from "../../../lib/queries/files";
import { useFormResponse } from "../../../lib/queries/formResponses";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Garde d'affichage : renvoie le `form_response_id` seulement si le payload
 * d'un intake_event désigne une réponse de formulaire (`origin === "form_response"`)
 * et porte un UUID valide. Sinon `null` → aucun panneau. Payload non typé
 * (`z.record(z.unknown())` côté wire) → lecture défensive.
 */
export function getFormResponseId(
  payload: Record<string, unknown> | null | undefined,
): string | null {
  if (!payload || payload.origin !== "form_response") return null;
  const id = payload.form_response_id;
  return typeof id === "string" && UUID_RE.test(id) ? id : null;
}

const sectionCls =
  "mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-7";

export function IntakeFormResponsePanel({ formResponseId }: { formResponseId: string }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const q = useFormResponse(formResponseId);
  const download = useFileDownloadUrl();
  const [downloadError, setDownloadError] = useState(false);
  async function openSigned() {
    if (!q.data?.mandate?.artifact_file_id) return;
    setDownloadError(false);
    try {
      const { url } = await download.mutateAsync(q.data.mandate.artifact_file_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setDownloadError(true);
    }
  }

  return (
    <section className={sectionCls} aria-labelledby="intake-form-response-title">
      <div>
        <h2
          id="intake-form-response-title"
          className="text-sm font-semibold uppercase tracking-wide text-slate-500"
        >
          {t("intake_events.detail.form_response.title")}
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          {t("intake_events.detail.form_response.help")}
        </p>
      </div>

      {q.data?.mandate && (
        <div className="space-y-2 rounded-lg border border-emerald-200 p-4">
          <h3 className="font-semibold">{t("request_flow.authorization")}</h3>
          <p>
            {q.data.mandate.signed_by_name} —{" "}
            {new Date(q.data.mandate.signed_at).toLocaleDateString(lang)}
          </p>
          {q.data.mandate.artifact_file_id ? (
            <button
              type="button"
              className="btn min-h-11"
              disabled={download.isPending}
              onClick={() => void openSigned()}
            >
              {t("request_flow.open_signed_pdf")}
            </button>
          ) : (
            <p role="alert">{t("request_flow.pdf_missing")}</p>
          )}
          {downloadError && <p role="alert">{t("request_flow.pdf_missing")}</p>}
        </div>
      )}
      {q.isLoading && !q.data ? (
        <p role="status" aria-live="polite" className="text-sm text-slate-500">
          {t("intake_events.detail.form_response.loading")}
        </p>
      ) : q.error || !q.data ? (
        // État neutre unique : 404 / absent / cross-tenant / supprimé — jamais de distinction.
        <div
          role="status"
          className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600"
        >
          {t("intake_events.detail.form_response.unavailable")}
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="break-words text-base font-semibold text-slate-900">
            {q.data.template.name}
          </h3>
          <p className="text-xs tabular-nums text-slate-500">
            {t("intake_events.detail.form_response.version_label", {
              number: q.data.version.version_number,
            })}
          </p>
          {q.data.response.project_id && (
            <Link
              to={`/projects/${q.data.response.project_id}#quote-requests`}
              className="focus-visible:ring-brand-500 text-brand-700 inline-flex min-h-11 items-center rounded-md px-2 text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("intake_events.detail.related_entities.view_project")}
            </Link>
          )}
          <FormRenderer
            schema={q.data.version.schema as unknown as RenderSchema}
            locale={lang}
            mode="readonly"
            values={q.data.response.data}
          />
        </div>
      )}
    </section>
  );
}
